import { useCallback, useContext, useEffect, useState } from 'react';
import { Layout, Table, Row, Col } from 'antd';
import moment from 'moment';
import { sessionStore, SESSION_ATTRS } from '../store/session';
import { getBookingRequests, approveRequest } from './actions';
import { notifiableAPICall, confirm } from '../utils/notification';
import { BOOKING_STATUS_ARRAY } from './constants';
import { clockIcon, locationPinIcon } from './svgIcons';
import { DeclinePopup } from './DeclinePopup';
import { RECURRENCES } from '../bookingSpace/constants';
import { SyncOutlined } from '@ant-design/icons'
import { RecurringApprovalModal } from '../bookingSpace/RecurringApprovalModal';

const { Content } = Layout;

export const BookingRequests = ({ bookingStatus, bookingSearchText, deskList, buildings, getFloorByFloorId }: any) => {

    const { state } = useContext(sessionStore);
    const user: any = state.get(SESSION_ATTRS.LOGGED_USER);

    const [bookingRequestList, setBookingRequestList] = useState<Array<any>>([]);
    const [filteredList, setFilteredList] = useState<Array<any>>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [aciveDeclineRequest, setActiveDeclinerequest] = useState<any>();
    const [recurringApprovalRequest, setRecurringApprovalRequest] = useState<undefined | Object>();

    const loadRequests = useCallback(async () => {
        try {
            const requestList = await getBookingRequests(user.buildingId, bookingStatus);
            setBookingRequestList(requestList);
            setFilteredList(requestList);
        }
        catch (err) {
            setBookingRequestList([]);
            setFilteredList([]);
        }
    }, [bookingStatus, user]);

    const loadBookingRequests = useCallback(async () => {
        setIsLoading(true);
        await loadRequests();
        setIsLoading(false);
    }, [loadRequests]);

    useEffect(() => {
        if (deskList !== undefined) {
            loadBookingRequests();
        }
    }, [loadBookingRequests, deskList]);

    const filterBookingRequests = useCallback((BookingRequests: Array<any>, searching: (string | undefined)) => {
        let filtered;
        if (searching) {
            const searchingText = searching.toLowerCase().trim();
            filtered = BookingRequests.filter(i => {
                const desk = deskList.find((desk: any) => desk.deskId === i.deskId)
                const phrase = desk.name + i.createdBy.name;
                return phrase.toLowerCase().includes(searchingText)
            });
        } else {
            filtered = BookingRequests;
        }
        // if (buildingId === ALL) return filtered;
        // return filtered.filter(i => i.buildingId === buildingId);
        return filtered;
    }, [deskList]);

    useEffect(() => {
        if (bookingRequestList.length !== 0) {
            setIsLoading(true);
            setFilteredList(filterBookingRequests(bookingRequestList, bookingSearchText));
            setIsLoading(false);
        }
    }, [bookingSearchText, bookingRequestList, filterBookingRequests]);

    const approveReq = async (requestId: string, req: any) => {
        const isOneTime = !req.recurrence || req.recurrence === RECURRENCES.ONETIME;
        if (!isOneTime) {
            setRecurringApprovalRequest(req);
            return;
        }
        confirm("Approve Booking", `Are you sure you want to approve the booking?`,
            async () => {
                await notifiableAPICall(() => approveRequest(user.buildingId, requestId),
                    'approve_req',
                    "Please wait...",
                    "Desk Booking Request Approved.",
                    "Something went wrong.",
                    true);
                loadBookingRequests();
            }, () => { });
    }

    const requestRoom = (deskId: string) => {
        const desk = deskList.find((desk: any) => desk.deskId === deskId);
        return (
            <>
                <div className="bs-title">
                    <div className="bs-title-img-wrapper fill">
                        <img className="bs-title-img" src={desk.thumbnailLink ? desk.thumbnailLink : '/images/no_image3.jpg'} alt={desk.name} />
                    </div>
                    <div className="bs-title-label">
                        <Row gutter={5} style={{ flexFlow: 'nowrap' }} className="bs-title-text">
                            <Col flex={4 / 5} style={{ maxWidth: 150, width: 'max-content' }}>{desk.name}</Col>
                            <Col flex={1 / 5} >{desk?.approvalRequired && <div style={{ marginLeft: 5 }}>{clockIcon}</div>}</Col>
                        </Row>
                        <Row gutter={5} className='bs-sub-title-text'>
                            <Col>
                                {locationPinIcon}
                            </Col>
                            <Col>
                                {getFloorByFloorId(desk.floorId)}
                            </Col>
                        </Row>
                    </div>
                </div>
            </>
        )
    }

    const dateTimeFormat = (row: any) => {
        if (moment(row.startTime).isSame(moment(row.endTime), 'day')) {
            return (
                <>
                    <Row>{moment(row.startTime).format("dddd, DD MMMM")}{row.recurrence && row.recurrence !== RECURRENCES.ONETIME && <SyncOutlined style={{ alignSelf: 'center', marginLeft: '10px', color: 'darkred' }} />}</Row>
                    <Row style={{ textAlign: 'left' }} className='bs-time-text'>{moment(row.startTime).format("hh:mm A") + ' - ' + moment(row.endTime).format("hh:mm A")}</Row>
                </>
            );
        }
        else {
            return (
                <>
                    <Row gutter={5}>
                        <Col style={{ marginRight: 3 }}>{moment(row.startTime).format("dddd, DD MMMM")}</Col>
                        <Col className='bs-time-text'>{moment(row.startTime).format("hh:mm A") + ' - '}</Col>
                    </Row>
                    <Row>
                        <Col style={{ marginRight: 3 }}>{moment(row.endTime).format("dddd, DD MMMM")}</Col>
                        <Col className='bs-time-text'>{moment(row.endTime).format("hh:mm A")}</Col>
                    </Row>
                </>
            );
        }
    }

    const columns: Array<any> = [
        {
            title: 'Desks',
            dataIndex: 'deskId',
            render: (deskId: string, row: any) => requestRoom(deskId)
        },
        {
            title: 'Date & Time',
            dataIndex: 'createdAt',
            render: (requestId: string, booking: any) => (
                <div className="bs-title-label" style={{ maxWidth: 150 }}>
                    {dateTimeFormat(booking)}
                    {booking.recurrence === RECURRENCES.ONETIME ? 'Does not Repeat' : booking.recurrence === RECURRENCES.DAILY ? 'Daily' : booking.recurrence === RECURRENCES.WEEKLY ? 'Weekly' : booking.recurrence === RECURRENCES.MONTHLY ? 'Monthly' : 'Does not Repeat'} {booking.recurrence && booking.recurrence !== RECURRENCES.ONETIME ? `(${booking.occurrence}/${booking.occurrences})` : ''}
                </div>
            )
        },
        {
            title: 'Building',
            dataIndex: 'buildingId',
            render: (buildingId: string) => (
                <div className="bs-title-label">
                    <Row className='bs-rw-sngl'>
                        <p>{buildings.find((b: any) => b.buildingId === buildingId)?.name}</p>
                    </Row>
                </div>)
        },
        {
            title: 'Booker',
            dataIndex: 'createdBy',
            render: (createdBy: any, row: any) => (
                <div className="bs-title-label">
                    <Row className='bs-rw-sngl'>
                        <p>{createdBy.name}</p>
                    </Row>
                </div>
            )
        },
        // {
        //     title: 'Reason for booking',
        //     dataIndex: 'bookingReason',
        //     render: (bookingReason: any, row: any) => (
        //         <div className="bs-title-label">
        //             <Row className='bs-rw'>
        //                 <Tooltip title={bookingReason ? bookingReason : "N/A"} trigger='hover'>
        //                     <p className='bs-txt-wrppr'>{bookingReason ? bookingReason : "N/A"}</p>
        //                 </Tooltip>
        //             </Row>
        //         </div>
        //     )
        // },
        {
            title: '',
            dataIndex: 'requestId',
            render: (requestId: string, req: any) => (
                bookingStatus !== BOOKING_STATUS_ARRAY[2].value && bookingStatus !== BOOKING_STATUS_ARRAY[3].value ?
                    <div className="bs-title-label">
                        <Row className='bs-rw' style={{ flexFlow: 'nowrap' }}>
                            {
                                // eslint-disable-next-line
                                <a className='link-btn link-btn--danger bs-lnk' onClick={() => setActiveDeclinerequest(req)} href="#">Decline</a>
                            }
                            {
                                bookingStatus !== BOOKING_STATUS_ARRAY[1].value ?
                                    // eslint-disable-next-line
                                    <a className='bs-br-btn' onClick={() => approveReq(requestId, req)} href="#">Approve</a>
                                    : null
                            }
                        </Row>
                    </div>
                    :
                    null
            )
        }
    ];

    return (
        <Content className="table-wrapper">
            {aciveDeclineRequest && <DeclinePopup isVisible={aciveDeclineRequest} onCancel={() => setActiveDeclinerequest(undefined)} request={aciveDeclineRequest} onOk={() => {
                setActiveDeclinerequest(undefined);
                loadBookingRequests();
            }} />}
            {recurringApprovalRequest && <RecurringApprovalModal isDeskBooking={true}
                onClose={() => setRecurringApprovalRequest(undefined)} request={recurringApprovalRequest}
                onOk={() => {
                    setRecurringApprovalRequest(undefined);
                    loadBookingRequests();
                }} />}
            <Table rowKey="requestId"
                columns={columns}
                dataSource={filteredList}
                loading={isLoading}
                pagination={{
                    total: filteredList.length,
                    showSizeChanger: true,
                    showTotal: (total, range) => `showing ${range[0]}-${range[1]} out of ${total} requests`
                }} />
        </Content>
    )
};
